import React from 'react';

import {
  IconAward,
  IconCalendar,
  IconRefresh,
  IconUserMinus,
} from '@tabler/icons-react';
import { Reassign } from 'icons';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/hooks/useAuth';

import PopoverMenuButton from '@/components/PopoverMenuButton/PopoverMenuButton';
import PopoverMenuButtonItem from '@/components/PopoverMenuButton/PopoverMenuButtonItem';
import { printTeamMember } from '@/components/print-report';
import { PrintCertificates } from '@/components/PrintCertificates';
import { PrintReportButton } from '@/components/PrintReportButton';

import ResetProgressManageMenuItem from './ResetProgressManageMenuItem';

interface ManageMenuProps {
  id: string;
  name: string;
  isComplete: boolean;
  isReports: boolean;
  isReportsPlansView: boolean;
  completedStepCount: number;
  handleRestartTraining: () => void;
  handleUnassign: () => void;
  handleUpdateDueDate: () => void;
  handleMarkComplete: () => void;
  userAllowedToUpdateTMDueDateOnPlan?: boolean;
  userCanMarkAsComplete?: boolean;
}

const ManageMenu: React.FC<ManageMenuProps> = ({
  id,
  name,
  isComplete,
  isReports,
  isReportsPlansView,
  completedStepCount,
  handleRestartTraining,
  handleUnassign,
  handleUpdateDueDate,
  handleMarkComplete,
  userAllowedToUpdateTMDueDateOnPlan = false,
  userCanMarkAsComplete = true,
}) => {
  const { t } = useTranslation();
  const { reassignPlansEpic: reassignPlansEpicFF } = useFlags();
  const { data: auth } = useAuth();

  const locationsWithAtLeastLeaderPermission =
    auth?.allLocationsWithAtLeastLeaderPermissions ?? [];

  const showRestartTrainingButton = reassignPlansEpicFF
    ? isComplete
    : completedStepCount !== 0 || isComplete;

  return (
    <div className="flex shrink-0 items-center justify-center gap-4">
      {isReports ? (
        <>
          {!isReportsPlansView && <PrintCertificates teamMemberId={id} />}
          <PrintReportButton
            onClick={e => {
              e.preventDefault();
              printTeamMember({ id, name });
            }}
          />
        </>
      ) : (
        <PopoverMenuButton
          data-testid="TeamMemberCardMenu"
          hideMenu={isReportsPlansView}
        >
          {showRestartTrainingButton && (
            <PopoverMenuButtonItem
              dataTestId="RestartTraining"
              icon={reassignPlansEpicFF ? <Reassign /> : <IconRefresh />}
              onClick={() => handleRestartTraining()}
              text={
                reassignPlansEpicFF
                  ? t('Button.reassign')
                  : t('Button.restartTraining')
              }
            />
          )}
          <PopoverMenuButtonItem
            dataTestId="UnassignTeamMember"
            icon={<IconUserMinus />}
            onClick={handleUnassign}
            text={t('Button.unassign')}
          />
          {!isComplete && userAllowedToUpdateTMDueDateOnPlan && (
            <PopoverMenuButtonItem
              dataTestId="UpdateDueDate"
              icon={<IconCalendar />}
              onClick={handleUpdateDueDate}
              text={t('Button.updateDueDate')}
            />
          )}
          {!isComplete &&
            !!locationsWithAtLeastLeaderPermission.length &&
            userCanMarkAsComplete && (
              <PopoverMenuButtonItem
                dataTestId="MarkCompleted"
                icon={<IconAward />}
                onClick={() => handleMarkComplete()}
                text={t('Button.markCompleted')}
              />
            )}
          <ResetProgressManageMenuItem
            isInProgress={completedStepCount > 0 && !isComplete}
            teamMemberId={id}
          />
        </PopoverMenuButton>
      )}
    </div>
  );
};

export default ManageMenu;
